import React, { FunctionComponent, useRef, useCallback } from "react";
import { Input, Form, InputNumber } from "antd";
import { ConfigProvider } from "@ui-kit/ConfigProvider";
import { Rule } from "antd/lib/form";
import { formatPhoneNumber } from "@lib/formatPhoneNumber";
import "./TextInput.css";

const { TextArea, Password } = Input;

const TextInput: FunctionComponent<TextInputProps> = ({
  type = "text",
  disabled,
  numberOfLines = 2,
  multiline,
  testID,
  accessibilityLabel,
  placeholder,
  size = "regular",
  onChange,
  name,
  maxLength = 6000,
  minNumberValue = 0,
  maxNumberValue,
  rules,
  hint,
  prefix,
  label,
  autoComplete,
  autoFocus,
  hidden,
}) => {
  let inputType: string;
  const ref = useRef(null);

  if (type === "telephoneNumber") {
    inputType = "tel";
  } else if (type === "emailAddress") {
    inputType = "email";
  } else {
    inputType = type;
  }

  const phoneParser = useCallback((value) => {
    return value.replace(/\D/g, "");
  }, []);

  return (
    <ConfigProvider type="regular">
      {multiline ? (
        <div style={{ position: "relative" }}>
          <Form.Item name={name} noStyle={true} rules={rules} hidden={hidden}>
            <TextArea
              rows={numberOfLines}
              disabled={disabled}
              placeholder={placeholder}
              aria-label={accessibilityLabel}
              data-testid={testID}
              onChange={onChange}
              maxLength={maxLength}
              ref={ref}
            />
          </Form.Item>
        </div>
      ) : inputType === "password" ? (
        <Form.Item name={name} noStyle={true} rules={rules} hidden={hidden}>
          <Password
            type={inputType}
            disabled={disabled}
            placeholder={placeholder}
            aria-label={accessibilityLabel}
            data-testid={testID}
            style={size === "large" ? { height: "46px" } : {}}
            onChange={onChange}
            maxLength={maxLength}
            autoComplete="current-password"
          />
        </Form.Item>
      ) : inputType === "number" ? (
        <Form.Item name={name} noStyle={true} rules={rules} hidden={hidden}>
          <InputNumber
            disabled={disabled}
            placeholder={placeholder}
            aria-label={accessibilityLabel}
            data-testid={testID}
            min={minNumberValue}
            max={maxNumberValue}
            onChange={onChange}
            style={size === "large" ? { height: "46px" } : {}}
            ref={ref}
          />
        </Form.Item>
      ) : inputType === "tel" ? (
        <Form.Item name={name} noStyle={true} rules={rules} hidden={hidden}>
          <InputNumber
            disabled={disabled}
            placeholder={placeholder}
            aria-label={accessibilityLabel}
            data-testid={testID}
            onChange={onChange}
            style={size === "large" ? { height: "46px" } : {}}
            ref={ref}
            autoComplete="tel"
            inputMode="tel"
            formatter={formatPhoneNumber}
            parser={phoneParser}
            controls={false}
            keyboard={false}
            stringMode={true}
          />
        </Form.Item>
      ) : inputType === "otp" ? (
        <Form.Item name={name} noStyle={true} rules={rules} hidden={hidden}>
          <Input
            disabled={disabled}
            placeholder={placeholder}
            aria-label={accessibilityLabel}
            data-testid={testID}
            onChange={onChange}
            style={size === "large" ? { height: "46px" } : {}}
            autoComplete="one-time-code"
            inputMode="numeric"
          />
        </Form.Item>
      ) : inputType === "email" ? (
        <Form.Item name={name} noStyle={true} rules={rules} hidden={hidden}>
          <Input
            disabled={disabled}
            placeholder={placeholder}
            aria-label={accessibilityLabel}
            data-testid={testID}
            onChange={onChange}
            style={size === "large" ? { height: "46px" } : {}}
            autoComplete={autoComplete}
            inputMode="email"
          />
        </Form.Item>
      ) : (
        <Form.Item
          name={name}
          label={label}
          noStyle={true}
          rules={rules}
          hidden={hidden}>
          <Input
            prefix={prefix}
            disabled={disabled}
            data-form-type="other"
            autoComplete={autoComplete}
            placeholder={placeholder}
            aria-label={accessibilityLabel}
            data-testid={testID}
            style={size === "large" ? { height: "46px" } : {}}
            onChange={onChange}
            maxLength={maxLength}
            ref={ref}
            autoFocus={autoFocus}
          />
        </Form.Item>
      )}
      {hint !== undefined && <span className="form-item-hint">{hint}</span>}
    </ConfigProvider>
  );
};

export interface Emoji {
  id: string;
  keywords: string[];
  name: string;
  native: string;
  shortcodes: string;
  unified: string;
}

/**
 * Auto complete hint for the input.
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
 */
export type AutoComplete =
  | "username"
  | "email"
  | "current-password"
  | "new-password"
  | "off"
  | "one-time-code"
  | "tel"
  | "name"
  | "postal-code";

export interface TextInputProps {
  /**
   * The initial value of the input
   */
  defaultValue?: string;
  /**
   * The label that appears above the input
   */
  label?: string;
  /**
   * Type of input.
   */
  type?:
    | "text"
    | "password"
    | "telephoneNumber"
    | "emailAddress"
    | "number"
    | "otp";
  /**
   * (Not Implemented) Hint message to assist user of intent.
   */
  hintMessage?: string;
  /**
   * Disable editing of the input
   */
  disabled?: boolean;
  /**
   * Display as a multi line text input
   */
  multiline?: boolean;
  /**
   * Number of lines, only applicable if `multiline` is true
   */
  numberOfLines?: number;
  /**
   * Test ID for selection in automated testing, sets `data-testid` attribute
   */
  testID?: string;
  /**
   * Sets `aria-label` for accessability
   */
  accessibilityLabel?: string;
  /**
   * Placeholder text displayed in input when there is no text
   */
  placeholder?: string;
  /**
   * Height of text input, large: 46px (not applicable to multiline)
   */
  size?: "small" | "regular" | "large";
  /**
   * Automatically passed in via Form.Item being the direct parent element.
   */
  onChange?: (event: any) => void;
  /**
   * Form input name.
   */
  name?: string | number | (string | number)[];
  /**
   * Maximum number of characters allowed in input.
   */
  maxLength?: number;
  /**
   * Minimum number value for number inputs.
   */
  minNumberValue?: number;
  /**
   * Maximum number value for number inputs.
   */
  maxNumberValue?: number;
  /*
   * Help text to display below the input.
   */
  hint?: string;
  /**
   *
   */
  prefix?: React.ReactNode;
  /*
   * Validation rules
   */
  rules?: Rule[];
  /**
   * Auto complete hint for the input.
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  autoComplete?: AutoComplete;

  autoFocus?: boolean;
  hidden?: boolean;
}

export { TextInput };
